import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, FileText, Building2, Store, TrendingUp, 
  BarChart3, MapPin, Users, Briefcase, Calendar, Download,
  ChevronRight, LineChart, DollarSign, Truck, Heart,
  Shield, Mail, Bell, Target, Home, Factory
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';


const MarketReportsPage = () => {
  const [emailInput, setEmailInput] = useState('');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Quarterly reports data
  const quarterlyReports = [
    {
      id: 1,
      quarter: 'Q4 2025',
      title: 'Houston Real Estate Market Report',
      focus: 'Greater Houston Metro',
      topics: ['Multifamily occupancy', 'Rent growth trends', 'Construction pipeline', 'Employment indicators'],
      status: 'Latest',
      date: 'January 2026'
    },
    {
      id: 2,
      quarter: 'Q3 2025',
      title: 'Houston Multifamily Market Analysis',
      focus: 'Multifamily Sector',
      topics: ['Submarket demand', 'Renovation activity', 'Absorption trends', 'Cap rate movements'],
      status: 'Available',
      date: 'October 2025'
    },
    {
      id: 3,
      quarter: 'Q2 2025',
      title: 'Commercial Real Estate Update — Houston Metro',
      focus: 'Commercial Assets',
      topics: ['Retail stability', 'Office absorption', 'Industrial demand', 'Lease trends'],
      status: 'Available',
      date: 'July 2025'
    },
    {
      id: 4,
      quarter: 'Q1 2025',
      title: 'Houston Investment Outlook',
      focus: 'Market Overview',
      topics: ['Economic forecasts', 'Investment activity', 'Submarket rankings', 'Risk factors'],
      status: 'Available',
      date: 'April 2025'
    }
  ];

  // Multifamily trend data
  const multifamilyMetrics = [
    { label: 'Occupancy Range', value: '92-96%', description: 'Across Houston submarkets' },
    { label: 'YoY Rent Growth', value: '3-5%', description: 'Value-add communities' },
    { label: 'New Supply', value: '15,000+', description: 'Units delivered annually' }
  ];

  // Economic drivers
  const economicDrivers = [
    { icon: Briefcase, title: 'Job Creation', description: 'Energy, healthcare, and logistics sectors driving employment growth' },
    { icon: Users, title: 'Population Growth', description: 'Continued migration supporting long-term housing demand' },
    { icon: Truck, title: 'Infrastructure', description: 'Port expansion and transportation investments' },
    { icon: Factory, title: 'Industry Diversification', description: 'Tech, medical, and manufacturing presence expanding' }
  ];

  // Report Card Component
  const ReportCard = ({ report }) => (
    <motion.div
      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl border border-white/10 overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-300 group"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
              <FileText className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <div>
              <span className="text-[#D4AF37] font-bold text-lg">{report.quarter}</span>
              <p className="text-white/40 text-xs">{report.date}</p>
            </div>
          </div>
          {report.status === 'Latest' && (
            <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold border border-emerald-500/30">
              Latest
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
          {report.title}
        </h3>

        {/* Focus */}
        <p className="text-white/50 text-sm mb-4">
          <span className="text-white/70">Focus:</span> {report.focus}
        </p>

        {/* Topics */}
        <div className="mb-5">
          <p className="text-white/40 text-xs mb-2">Coverage:</p>
          <div className="flex flex-wrap gap-2">
            {report.topics.map((topic, index) => (
              <span 
                key={index}
                className="px-2 py-1 rounded-md bg-white/5 text-white/60 text-xs border border-white/5"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Button 
          className="w-full bg-white/5 hover:bg-[#D4AF37]/10 text-white hover:text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]/30 rounded-lg transition-all"
          variant="outline"
        >
          View Report
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );

  return (
    <>
      <Helmet>
        <title>Houston Real Estate Market Reports & Trends | ASAP Capital</title>
        <meta 
          name="description" 
          content="Access quarterly Houston real estate market reports covering multifamily and commercial trends, economic indicators, and investment insights." 
        />
        <meta name="keywords" content="Houston real estate market reports, multifamily market trends, commercial real estate analysis, Texas real estate research, Houston market data" />
        <link rel="canonical" href="/market-reports" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                {/* Header */}
                <div className="max-w-3xl">
                  <motion.div 
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                    variants={itemVariants}
                  >
                    <BarChart3 className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Market Research</span>
                  </motion.div>

                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                    variants={itemVariants}
                  >
                    Houston Real Estate{' '}
                    <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                      Market Reports
                    </span>
                  </motion.h1>

                  <motion.div 
                    className="space-y-4 text-white/60 text-base lg:text-lg leading-relaxed"
                    variants={itemVariants}
                  >
                    <p>
                      ASAP Capital's Houston real estate market reports provide data-driven insights 
                      into multifamily and commercial property trends across Greater Houston and 
                      select Texas markets. These reports are designed to support investor education, 
                      market research, and long-term investment analysis.
                    </p>
                    <p>
                      Our research focuses on rental demand, supply pipelines, employment growth, 
                      and neighborhood-level fundamentals that influence value-add real estate 
                      investment strategies.
                    </p>
                  </motion.div>

                  <motion.p 
                    className="mt-6 text-white/40 text-sm italic border-l-2 border-[#D4AF37]/30 pl-4"
                    variants={itemVariants}
                  >
                    Market data is provided for informational purposes only and does not constitute investment advice.
                  </motion.p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== QUARTERLY MARKET REPORTS ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                    <Calendar className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Quarterly Market Reports
                  </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-6">
                  {quarterlyReports.map((report) => (
                    <ReportCard key={report.id} report={report} />
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MULTIFAMILY MARKET TRENDS ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Left - Content */}
                  <motion.div variants={itemVariants}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                        <Building2 className="h-5 w-5 text-blue-400" />
                      </div>
                      <h2 className="text-2xl lg:text-3xl font-bold text-white">
                        Multifamily Market Trends
                      </h2>
                    </div>

                    <div className="space-y-4 text-white/60 leading-relaxed">
                      <p>
                        Greater Houston continues to demonstrate strong multifamily fundamentals 
                        supported by population growth, job creation, and long-term housing demand. 
                        Our multifamily market analysis reviews occupancy levels, rent growth patterns, 
                        and new supply pipelines across key suburban and urban submarkets.
                      </p>
                      <p>
                        We track performance indicators across workforce and value-add communities 
                        to assess operational opportunities and long-term stabilization potential.
                      </p>
                    </div>
                  </motion.div>

                  {/* Right - Metrics */}
                  <motion.div 
                    className="space-y-4"
                    variants={itemVariants}
                  >
                    {multifamilyMetrics.map((metric, index) => (
                      <motion.div
                        key={metric.label}
                        className="bg-gradient-to-r from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10 hover:border-blue-500/30 transition-all"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white/50 text-sm mb-1">{metric.label}</p>
                            <p className="text-white/40 text-xs">{metric.description}</p>
                          </div>
                          <p className="text-2xl font-bold text-blue-400">{metric.value}</p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== COMMERCIAL MARKET INSIGHTS ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                    <Store className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Commercial Market Insights
                  </h2>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-8">
                  <motion.div variants={itemVariants}>
                    <p className="text-white/60 leading-relaxed mb-6">
                      Houston's commercial real estate markets benefit from economic diversification, 
                      logistics activity, and neighborhood-serving retail demand. Our commercial real 
                      estate reports review trends across retail centers, office properties, and 
                      service-oriented commercial assets.
                    </p>
                    <p className="text-white/60 leading-relaxed">
                      These insights help evaluate tenant demand stability, lease structures, 
                      and submarket resilience.
                    </p>
                  </motion.div>

                  <motion.div 
                    className="grid grid-cols-1 gap-4"
                    variants={itemVariants}
                  >
                    {[
                      { icon: Store, title: 'Neighborhood Retail Centers', color: 'emerald' },
                      { icon: Heart, title: 'Medical Office Corridors', color: 'rose' },
                      { icon: Building2, title: 'Suburban Office Nodes', color: 'blue' }
                    ].map((asset, index) => (
                      <div 
                        key={asset.title}
                        className={`flex items-center gap-4 p-4 rounded-xl bg-${asset.color}-500/5 border border-${asset.color}-500/20`}
                        style={{
                          background: asset.color === 'emerald' ? 'rgba(16, 185, 129, 0.05)' :
                                     asset.color === 'rose' ? 'rgba(244, 63, 94, 0.05)' :
                                     'rgba(59, 130, 246, 0.05)',
                          borderColor: asset.color === 'emerald' ? 'rgba(16, 185, 129, 0.2)' :
                                       asset.color === 'rose' ? 'rgba(244, 63, 94, 0.2)' :
                                       'rgba(59, 130, 246, 0.2)'
                        }}
                      >
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{
                            background: asset.color === 'emerald' ? 'rgba(16, 185, 129, 0.1)' :
                                       asset.color === 'rose' ? 'rgba(244, 63, 94, 0.1)' :
                                       'rgba(59, 130, 246, 0.1)'
                          }}
                        >
                          <asset.icon 
                            className="h-5 w-5"
                            style={{
                              color: asset.color === 'emerald' ? '#34d399' :
                                     asset.color === 'rose' ? '#fb7185' :
                                     '#60a5fa'
                            }}
                          />
                        </div>
                        <span className="text-white font-medium">{asset.title}</span>
                      </div>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== ECONOMIC DRIVERS ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="text-center mb-12" variants={itemVariants}>
                  <div className="inline-flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                      <TrendingUp className="h-5 w-5 text-purple-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Economic Drivers
                    </h2>
                  </div>
                  <p className="text-white/50 max-w-2xl mx-auto">
                    Houston's long-term real estate performance is influenced by employment growth, 
                    population migration, infrastructure investment, and industry diversification.
                  </p>
                </motion.div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {economicDrivers.map((driver, index) => (
                    <motion.div
                      key={driver.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-purple-500/30 transition-all text-center"
                      variants={itemVariants}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-14 h-14 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20 mx-auto mb-4">
                        <driver.icon className="h-7 w-7 text-purple-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{driver.title}</h3>
                      <p className="text-white/50 text-sm">{driver.description}</p>
                    </motion.div>
                  ))}
                </div>

                <motion.p 
                  className="text-white/40 text-sm text-center mt-8"
                  variants={itemVariants}
                >
                  These drivers are evaluated alongside property-level data to align investment 
                  strategy with macroeconomic conditions.
                </motion.p>
              </motion.div>
            </div>
          </section>

          {/* ==================== STRATEGY ALIGNMENT ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                <motion.div variants={itemVariants}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                      <Target className="h-5 w-5 text-amber-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Strategy Alignment
                    </h2>
                  </div>

                  <div className="space-y-4 text-white/60 leading-relaxed mb-6">
                    <p>
                      Market research informs underwriting assumptions, renovation planning, 
                      and submarket selection within our value-add real estate investment strategy. 
                      By aligning capital deployment with verified demand drivers, we seek to 
                      prioritize operational improvements over market timing.
                    </p>
                    <p>
                      Market reports help validate assumptions related to rental growth, 
                      tenant stability, and long-term asset sustainability.
                    </p>
                  </div>

                  <Link 
                    to="/strategy"
                    className="inline-flex items-center gap-2 text-[#D4AF37] font-medium hover:gap-3 transition-all"
                  >
                    View Our Investment Strategy
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </motion.div>

                <motion.div 
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10"
                  variants={itemVariants}
                >
                  <h4 className="text-white font-semibold mb-6">How Market Data Informs Decisions</h4>
                  <div className="space-y-4">
                    {[
                      { step: '01', title: 'Submarket Analysis', description: 'Identify high-potential neighborhoods' },
                      { step: '02', title: 'Demand Validation', description: 'Confirm rental growth potential' },
                      { step: '03', title: 'Risk Assessment', description: 'Evaluate supply pipeline impacts' },
                      { step: '04', title: 'Exit Planning', description: 'Model stabilization timelines' }
                    ].map((item, index) => (
                      <div key={item.step} className="flex items-start gap-4">
                        <span className="text-[#D4AF37] font-bold text-lg">{item.step}</span>
                        <div>
                          <h5 className="text-white font-medium">{item.title}</h5>
                          <p className="text-white/40 text-sm">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PORTFOLIO AND CASE STUDIES ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="text-center mb-10" variants={itemVariants}>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                    Portfolio and Case Studies
                  </h2>
                  <p className="text-white/50 max-w-2xl mx-auto">
                    Explore how market trends translate into executed investments through our 
                    active and realized portfolio as well as detailed property-level case studies.
                  </p>
                </motion.div>

                <motion.div 
                  className="grid md:grid-cols-2 gap-6"
                  variants={itemVariants}
                >
                  <Link to="/portfolio">
                    <motion.div
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10 hover:border-[#D4AF37]/30 transition-all group h-full"
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20 mb-6">
                        <Home className="h-7 w-7 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
                        View Investment Portfolio
                      </h3>
                      <p className="text-white/50 mb-4">
                        Explore our active and realized investments across Houston's multifamily 
                        and commercial markets.
                      </p>
                      <span className="inline-flex items-center gap-2 text-[#D4AF37] font-medium group-hover:gap-3 transition-all">
                        Explore Portfolio <ArrowRight className="h-4 w-4" />
                      </span>
                    </motion.div>
                  </Link>

                  <Link to="/case-studies">
                    <motion.div
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10 hover:border-blue-500/30 transition-all group h-full"
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 mb-6">
                        <Briefcase className="h-7 w-7 text-blue-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                        View Case Studies
                      </h3>
                      <p className="text-white/50 mb-4">
                        Detailed property-level examples showing value creation from acquisition 
                        through stabilization.
                      </p>
                      <span className="inline-flex items-center gap-2 text-blue-400 font-medium group-hover:gap-3 transition-all">
                        View Case Studies <ArrowRight className="h-4 w-4" />
                      </span>
                    </motion.div>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MARKET UPDATES / SUBSCRIBE ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                {/* Background accents */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center">
                  {/* Content */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                        <Bell className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <h2 className="text-2xl lg:text-3xl font-bold text-white">
                        Market Updates
                      </h2>
                    </div>
                    <p className="text-white/50 mb-6">
                      New market reports and investment insights are released periodically. 
                      Subscribe to receive updates on Houston real estate market trends, 
                      multifamily performance indicators, and commercial property research.
                    </p>
                    
                    {/* Email signup */}
                    <div className="flex gap-3 mb-6">
                      <div className="relative flex-1">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                        <input
                          type="email"
                          placeholder="Enter your email"
                          value={emailInput}
                          onChange={(e) => setEmailInput(e.target.value)}
                          className="w-full pl-11 pr-4 py-3 rounded-full bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                        />
                      </div>
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-6 rounded-full">
                        Subscribe
                      </Button>
                    </div>

                    <Link 
                      to="/contact"
                      
                      
                      className="inline-flex items-center gap-2 text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
                    >
                      Or schedule a consultation
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>

                  {/* Quick links */}
                  <div className="bg-[#0A1628]/50 rounded-2xl p-6 border border-white/5">
                    <h4 className="text-white font-semibold mb-4">Related Resources</h4>
                    <div className="space-y-3">
                      {[
                        { name: 'Why Houston', href: '/why-houston', icon: MapPin },
                        { name: 'Investment Strategy', href: '/strategy', icon: Target },
                        { name: 'Track Record', href: '/track-record', icon: TrendingUp },
                        { name: 'Market Insights', href: '/insights', icon: LineChart }
                      ].map((link) => (
                        <Link 
                          key={link.name}
                          to={link.href}
                          className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-[#D4AF37]/10 border border-white/5 hover:border-[#D4AF37]/30 transition-all group"
                        >
                          <link.icon className="h-4 w-4 text-[#D4AF37]" />
                          <span className="text-white/70 text-sm group-hover:text-white transition-colors">{link.name}</span>
                          <ChevronRight className="h-4 w-4 text-white/30 ml-auto group-hover:text-[#D4AF37] transition-colors" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== COMPLIANCE FOOTER ==================== */}
          <section className="py-8 relative border-t border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="bg-white/[0.02] border border-white/5 rounded-xl p-5"
              >
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-white/30 flex-shrink-0 mt-0.5" />
                  <p className="text-white/40 text-xs leading-relaxed">
                    <strong className="text-white/50">Important Disclosure:</strong> Market data is provided 
                    for informational purposes only and does not constitute investment advice. All investments 
                    involve risk. Past performance is not indicative of future results. Please refer to 
                    offering documents and PPMs for full investment terms.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default MarketReportsPage;
